import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Cookie, Download, FileText, Pencil, Trash2, Undo2 } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { paths } from '@/app/paths';
import { cn } from '@/design-system/lib/cn';
import { Badge } from '@/design-system/ui/Badge';
import { Button } from '@/design-system/ui/Button';
import { Dialog, DialogBody, DialogContent, DialogHeader } from '@/design-system/ui/Dialog';
import { Skeleton } from '@/design-system/ui/Skeleton';
import { Switch } from '@/design-system/ui/Switch';
import { formatNumericDate, toISODate } from '@escala/domain/dates';
import { LEGAL_VERSION, PRIVACY_CONTACT, consentSummary } from '@escala/domain/legal';
import {
  COMMUNICATION_LABEL,
  PRIVACY_REQUEST_LABEL,
  PRIVACY_STATUS_LABEL,
  exportFileName,
  requestsOf,
} from '@escala/domain/privacy';
import type { CommunicationPrefs, PrivacyRequest, PrivacyRequestKind } from '@escala/domain/types';
import { useCookieConsent } from '@/privacy/consent';
import { useStore } from '@/store/AppStore';
import { PRIVACY_ACCOUNT_KEY } from '@/store/privacy';

import { useLegal } from './context';
import PrivacyRequestDialog from './PrivacyRequestDialog';

const COMMUNICATIONS = Object.keys(COMMUNICATION_LABEL) as (keyof CommunicationPrefs)[];

const STATUS_TONE: Record<PrivacyRequest['status'], 'partial' | 'complete' | 'conflict'> = {
  open: 'partial',
  done: 'complete',
  denied: 'conflict',
};

/** "15/10/2026" a partir de um instante (ISO ou epoch). */
const dayOf = (at: string | number) => formatNumericDate(toISODate(new Date(at)));

/** Baixa o JSON como arquivo; `false` quando o navegador não deixa. */
function download(data: unknown, filename: string): boolean {
  if (typeof URL.createObjectURL !== 'function') return false;

  const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }));
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);

  return true;
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section aria-label={title} className="flex flex-col gap-2">
      <h3 className="m-0 text-caption font-bold tracking-[0.05em] text-ink-subtle uppercase">{title}</h3>
      {children}
    </section>
  );
}

function Row({ title, description, children }: { title: string; description: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-block border border-line-card px-3.5 py-3">
      <div className="min-w-50 flex-1">
        <p className="m-0 font-semibold">{title}</p>
        <p className="m-0 text-meta text-ink-subtle">{description}</p>
      </div>
      {children}
    </div>
  );
}

function RightButton({
  icon,
  title,
  description,
  danger,
  disabled,
  onClick,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  danger?: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex items-start gap-2.5 rounded-block border bg-white px-3.5 py-3 text-left disabled:cursor-not-allowed disabled:opacity-50',
        '[&_svg]:mt-0.5 [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:stroke-[1.8]',
        danger
          ? 'border-status-conflict-line text-status-conflict hover:bg-danger-soft'
          : 'border-line-card text-primary hover:border-primary-line hover:bg-primary-faint'
      )}
    >
      {icon}
      <span className="flex flex-col">
        <span className={cn('text-control font-semibold', danger ? 'text-status-conflict' : 'text-ink')}>
          {title}
        </span>
        <span className="text-meta text-ink-subtle">{description}</span>
      </span>
    </button>
  );
}

/**
 * "Privacidade e dados" (menu do usuário): os consentimentos (a versão dos Termos aceita e
 * a escolha de cookies), as comunicações (cada canal liga e desliga), os direitos do
 * titular — baixar os dados, corrigir, revogar o consentimento, excluir a conta — e os
 * pedidos enviados, com a situação de cada um.
 */
export default function PrivacyCenterDialog({ onClose }: { onClose: () => void }) {
  const { state, me, org, guest, privacy, actions } = useStore();
  const { openLegal, openCookies } = useLegal();
  const consent = useCookieConsent();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [requesting, setRequesting] = useState<PrivacyRequestKind | null>(null);
  const key = [...PRIVACY_ACCOUNT_KEY, me.id];
  const account = useQuery({ queryKey: key, queryFn: privacy.account });
  const communications = useMutation({
    mutationFn: privacy.setCommunications,
    onSuccess: (fresh) => queryClient.setQueryData(key, fresh),
    onError: () => actions.showToast('Não foi possível salvar agora. Tente de novo.', { tone: 'error' }),
  });
  const exporting = useMutation({
    mutationFn: privacy.exportData,
    onSuccess: (data) => {
      if (!data) return actions.showToast('Você não tem cadastro nesta organização.', { tone: 'error' });

      const ok = download(data, exportFileName(new Date()));

      actions.showToast(ok ? 'Arquivo com os seus dados baixado.' : 'Não foi possível baixar o arquivo.', {
        tone: ok ? 'success' : 'error',
      });
    },
    onError: () => actions.showToast('Não foi possível gerar a cópia agora.', { tone: 'error' }),
  });
  const requests = requestsOf(state.privacyRequests, me.id);
  const accepted = account.data;
  // Enquanto salva, o interruptor já mostra a escolha nova.
  const prefs: Partial<CommunicationPrefs> = {
    ...accepted?.communications,
    ...(communications.isPending ? communications.variables : {}),
  };

  return (
    <>
      <Dialog open onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="w-[min(620px,calc(100vw-24px))]" described>
          <DialogHeader
            title="Privacidade e dados"
            description="Seus consentimentos e direitos como titular dos dados (LGPD)"
          />
          <DialogBody className="gap-5">
            <Section title="Consentimentos">
              {accepted ? (
                <Row
                  title={
                    accepted.termsVersion
                      ? `Termos e Política de Privacidade · versão ${accepted.termsVersion}`
                      : 'Termos e Política de Privacidade'
                  }
                  description={
                    accepted.termsAcceptedAt
                      ? `Aceitos em ${dayOf(accepted.termsAcceptedAt)}${accepted.termsVersion === LEGAL_VERSION ? '' : ` · a versão atual é a ${LEGAL_VERSION}`}`
                      : 'Ainda não aceitos nesta conta.'
                  }
                >
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => openLegal('privacy', { orgName: org.name })}
                  >
                    <FileText aria-hidden />
                    Ler
                  </Button>
                </Row>
              ) : (
                <Skeleton className="h-16 w-full" />
              )}
              <Row
                title={`Cookies: ${consentSummary(consent).toLowerCase()}`}
                description={
                  consent ? `Escolhidos em ${dayOf(consent.decidedAt)}, neste navegador` : 'Neste navegador'
                }
              >
                <Button variant="secondary" size="sm" onClick={openCookies}>
                  <Cookie aria-hidden />
                  Gerenciar cookies
                </Button>
              </Row>
            </Section>

            <Section title="Comunicações">
              <div className="overflow-hidden rounded-block border border-line-card">
                {COMMUNICATIONS.map((channel) => (
                  <div
                    key={channel}
                    className="flex items-center gap-3 border-b border-line-divider px-3.5 py-2.75 last:border-0"
                  >
                    <div className="flex-1">
                      <p className="m-0 font-semibold">{COMMUNICATION_LABEL[channel].title}</p>
                      <p className="m-0 text-meta text-ink-subtle">
                        {COMMUNICATION_LABEL[channel].description}
                      </p>
                    </div>
                    <Switch
                      aria-label={COMMUNICATION_LABEL[channel].title}
                      checked={prefs[channel] ?? true}
                      disabled={!accepted || communications.isPending}
                      onCheckedChange={(on) => communications.mutate({ [channel]: on })}
                    />
                  </div>
                ))}
              </div>
              <p className="m-0 text-meta text-ink-subtle">
                Avisos essenciais de segurança e de acesso (convite, senha) são sempre enviados.
              </p>
            </Section>

            <Section title="Seus direitos">
              <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,240px),1fr))] gap-2">
                <RightButton
                  icon={<Download aria-hidden />}
                  title="Baixar meus dados"
                  description="Cópia completa em arquivo JSON"
                  disabled={guest || exporting.isPending}
                  onClick={() => exporting.mutate()}
                />
                <RightButton
                  icon={<Pencil aria-hidden />}
                  title="Corrigir meus dados"
                  description="Abre o seu perfil para edição"
                  disabled={guest}
                  onClick={() => {
                    onClose();
                    navigate(paths.me);
                  }}
                />
                <RightButton
                  icon={<Undo2 aria-hidden />}
                  title={PRIVACY_REQUEST_LABEL.revoke.title}
                  description="Para dados tratados com a sua autorização"
                  disabled={guest}
                  onClick={() => setRequesting('revoke')}
                />
                <RightButton
                  icon={<Trash2 aria-hidden />}
                  title={PRIVACY_REQUEST_LABEL.deletion.title}
                  description={PRIVACY_REQUEST_LABEL.deletion.description}
                  danger
                  disabled={guest}
                  onClick={() => setRequesting('deletion')}
                />
              </div>
              <Button
                variant="link"
                size="sm"
                className="self-start"
                disabled={guest}
                onClick={() => setRequesting('other')}
              >
                Fazer outro pedido sobre os meus dados
              </Button>
              {requests.length ? (
                <ul
                  aria-label="Seus pedidos"
                  className="m-0 flex list-none flex-col overflow-hidden rounded-block border border-line-card p-0"
                >
                  {requests.map((r) => (
                    <li
                      key={r.id}
                      className="flex flex-col gap-1 border-b border-line-divider px-3.5 py-2.75 last:border-0"
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="flex-1 text-small">
                          <strong>{PRIVACY_REQUEST_LABEL[r.kind].title}</strong>
                          <span className="text-ink-subtle"> · enviado em {dayOf(r.createdAt)}</span>
                        </span>
                        <Badge size="sm" tone={STATUS_TONE[r.status]}>
                          {PRIVACY_STATUS_LABEL[r.status]}
                        </Badge>
                      </div>
                      <p className="m-0 text-meta text-ink-subtle">
                        {r.status === 'open'
                          ? `A igreja responde até ${formatNumericDate(r.dueDate)}.`
                          : `Respondido em ${dayOf(r.resolvedAt ?? r.createdAt)}${r.response ? `: ${r.response}` : '.'}`}
                      </p>
                    </li>
                  ))}
                </ul>
              ) : null}
            </Section>

            <p className="m-0 text-meta text-pretty text-ink-subtle">
              Quem decide sobre os seus dados é a {org.name}; a Live Gospel os trata em nome dela. Encarregado
              de dados (DPO): <strong className="text-ink-secondary">{PRIVACY_CONTACT.email}</strong>. Você
              também pode recorrer à ANPD.
            </p>
          </DialogBody>
        </DialogContent>
      </Dialog>
      {requesting ? <PrivacyRequestDialog kind={requesting} onClose={() => setRequesting(null)} /> : null}
    </>
  );
}
